import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { ADMIN_EMAIL } from "../config";
import {
  DEFAULT_RULES_MARKDOWN,
  DEFAULT_RULES_TITLE,
  RULES_SLUG,
} from "../content/rulesMarkdown";
import Markdown from "./Markdown";
import { fadeUp } from "./ui/motion";

const slugify = (text) =>
  String(text).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/** Splits the rulebook on `## ` headings: { title, intro, sections: [{ id, heading, body }] }. */
export const splitRules = (markdown, fallbackTitle) => {
  const lines = String(markdown || "").split("\n");
  let title = fallbackTitle;
  const intro = [];
  const sections = [];
  lines.forEach((line) => {
    const h1 = /^#\s+(.+)/.exec(line);
    const h2 = /^##\s+(.+)/.exec(line);
    if (h1 && !sections.length && !intro.some((l) => l.trim())) {
      title = h1[1].trim();
    } else if (h2) {
      sections.push({ id: slugify(h2[1]), heading: h2[1].trim(), body: [] });
    } else if (sections.length) {
      sections[sections.length - 1].body.push(line);
    } else {
      intro.push(line);
    }
  });
  return {
    title,
    intro: intro.join("\n").trim(),
    sections: sections.map((sec) => ({ ...sec, body: sec.body.join("\n").trim() })),
  };
};

const RulesView = ({ markdown, fallbackTitle, updatedLabel, action }) => {
  const { title, intro, sections } = splitRules(markdown, fallbackTitle);
  const [league, rest] = title.includes("—") ? title.split("—").map((t) => t.trim()) : ["HSL", title];
  return (
    <Box>
      <Box sx={{ bgcolor: "#0a0a0a", color: "#fff", borderRadius: { xs: 0, sm: 4 }, px: { xs: 2.5, sm: 4 }, py: { xs: 3.5, sm: 5 }, position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", right: -80, top: -80, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.14), rgba(255,255,255,0) 70%)" }} />
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", opacity: 0.65 }}>{league}</Typography>
        <Typography component="h1" sx={{ fontSize: { xs: "2rem", sm: "2.8rem" }, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.05, mt: 0.5 }}>
          {rest}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
          {updatedLabel && <Typography sx={{ opacity: 0.65, fontSize: "0.85rem" }}>Updated {updatedLabel}</Typography>}
          {action}
        </Box>
        {sections.length > 1 && (
          <Box component="nav" aria-label="Rulebook sections" sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 3 }}>
            {sections.map((sec, i) => (
              <Box
                key={sec.id}
                component="a"
                href={`#${sec.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(sec.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                sx={{ color: "#fff", textDecoration: "none", fontSize: "0.82rem", fontWeight: 700, px: 1.5, py: 0.6, borderRadius: 999, border: "1px solid rgba(255,255,255,0.25)", bgcolor: "rgba(255,255,255,0.06)", "&:hover": { bgcolor: "rgba(255,255,255,0.16)" } }}
              >
                <Box component="span" sx={{ opacity: 0.55, mr: 0.75 }}>{String(i + 1).padStart(2, "0")}</Box>
                {sec.heading}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {intro && (
        <Box sx={{ mt: 2, px: { xs: 2, sm: 0 } }}>
          <Markdown source={intro} />
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2, px: { xs: 1.5, sm: 0 } }}>
        {sections.map((sec, i) => (
          <Box key={sec.id} id={sec.id} component="section" sx={{ scrollMarginTop: 80, bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 4, p: { xs: 2, sm: 3 }, boxShadow: "0 1px 2px rgba(16,24,40,0.04)", ...fadeUp(i) }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Box sx={{ width: 34, height: 34, borderRadius: 2.5, bgcolor: "#0a0a0a", color: "#fff", fontWeight: 900, fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {String(i + 1).padStart(2, "0")}
              </Box>
              <Typography component="h2" sx={{ fontWeight: 900, fontSize: "1.15rem", letterSpacing: "-0.01em" }}>{sec.heading}</Typography>
            </Box>
            <Markdown source={sec.body} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const formatUpdatedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "America/Toronto",
  });
};

/**
 * Public rulebook at `/rules`. The text lives in `public.league_rules` so the
 * commissioner can edit it in place; the copy bundled in `content/rulesMarkdown`
 * is the fallback for when that row isn't there yet.
 */
const Rules = () => {
  const { user } = useAuth();
  const isAdmin =
    (user?.email || "").trim().toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const [doc, setDoc] = useState({
    title: DEFAULT_RULES_TITLE,
    markdown: DEFAULT_RULES_MARKDOWN,
    updatedAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedNotice, setSavedNotice] = useState(false);

  const loadRules = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from("league_rules")
      .select("title, content_markdown, updated_at")
      .eq("slug", RULES_SLUG)
      .maybeSingle();

    if (fetchError) throw fetchError;
    if (!data) return null;

    return {
      title: data.title || DEFAULT_RULES_TITLE,
      markdown: data.content_markdown || DEFAULT_RULES_MARKDOWN,
      updatedAt: data.updated_at || null,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadRules()
      .then((row) => {
        if (cancelled || !row) return;
        setDoc(row);
      })
      .catch((e) => {
        // A missing table or row just means the published rulebook hasn't been
        // seeded yet, so keep showing the bundled copy.
        console.error("Error fetching rules:", e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadRules]);

  const startEditing = () => {
    setDraft(doc.markdown);
    setError("");
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const { data, error: saveError } = await supabase
        .from("league_rules")
        .upsert(
          {
            slug: RULES_SLUG,
            title: doc.title,
            content_markdown: draft,
            updated_by: user?.email ?? null,
          },
          { onConflict: "slug" }
        )
        .select("title, content_markdown, updated_at")
        .single();

      if (saveError) throw saveError;

      setDoc({
        title: data.title || DEFAULT_RULES_TITLE,
        markdown: data.content_markdown || "",
        updatedAt: data.updated_at || null,
      });
      setEditing(false);
      setSavedNotice(true);
    } catch (e) {
      setError(
        e?.message ||
          "Could not save the rules. Make sure you are signed in as the commissioner."
      );
    } finally {
      setSaving(false);
    }
  };

  const updatedLabel = formatUpdatedAt(doc.updatedAt);

  const editButton = isAdmin && !editing && (
    <Button
      size="small"
      startIcon={<EditIcon />}
      onClick={startEditing}
      sx={{ color: "#fff", fontWeight: 700, borderRadius: 999, border: "1px solid rgba(255,255,255,0.3)", px: 1.5 }}
    >
      Edit
    </Button>
  );

  return (
    <Box sx={{ maxWidth: 860, mx: "auto", px: { xs: 0, sm: 2 }, pt: { xs: 0, sm: 3 }, pb: 6 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ px: { xs: 1.5, sm: 0 } }}>
          <Skeleton variant="rounded" height={200} sx={{ borderRadius: 4 }} />
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: 4, mt: 1.5 }} />
          ))}
        </Box>
      ) : editing ? (
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 }, border: "1px solid #eee", borderRadius: 4 }}>
          <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 1 }}>
            Markdown: <code># Title</code>, <code>## Section</code>, numbered or bulleted lists, <code>**bold**</code>, <code>*italic*</code>.
          </Typography>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            multiline
            minRows={18}
            fullWidth
            spellCheck
            inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
          />
          <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !draft.trim()}
              sx={{ backgroundColor: "#0a0a0a", fontWeight: 700, borderRadius: 999, px: 3, "&:hover": { backgroundColor: "#222" } }}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              onClick={() => {
                setEditing(false);
                setError("");
              }}
              disabled={saving}
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Cancel
            </Button>
          </Box>
          <Typography variant="overline" sx={{ display: "block", color: "#888", mt: 3, mb: 1 }}>
            Preview
          </Typography>
          <RulesView markdown={draft} fallbackTitle={doc.title} />
        </Paper>
      ) : (
        <RulesView markdown={doc.markdown} fallbackTitle={doc.title} updatedLabel={updatedLabel} action={editButton} />
      )}

      <Snackbar
        open={savedNotice}
        autoHideDuration={4000}
        onClose={() => setSavedNotice(false)}
        message="Rules updated"
      />
    </Box>
  );
};

export default Rules;
